/**
 * RuleSettingsBar - Unified template-level settings for verdict rules
 *
 * Always visible above verdict rule cards. Contains:
 * - Rolling window (always shown): how many days of KPI data to use
 * - Percentile settings (conditional): shown when percentile operators are used
 *
 * Progressive disclosure: rolling window is always relevant; percentile settings
 * only appear when needed (Phase 26 self-revealing pattern preserved).
 */

import { Clock, HelpCircle } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import type { PercentileSettings } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

export interface RuleSettingsBarProps {
  settings: PercentileSettings;
  onChange: (settings: PercentileSettings) => void;
  rollingWindowDays: number;
  onRollingWindowDaysChange: (days: number) => void;
  showPercentileSettings: boolean;
}

// ============================================================================
// SCOPE OPTIONS
// ============================================================================

const SCOPE_OPTIONS: { value: PercentileSettings['defaultScope']; label: string }[] = [
  { value: 'smart', label: 'Smart default' },
  { value: 'goal', label: 'By Goal' },
  { value: 'bucket', label: 'By Objective' },
  { value: 'all', label: 'All Project' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function RuleSettingsBar({
  settings,
  onChange,
  rollingWindowDays,
  onRollingWindowDaysChange,
  showPercentileSettings,
}: RuleSettingsBarProps) {
  const inputClass =
    'border border-blue-200 rounded-lg px-2 py-1 text-sm bg-surface w-16 focus:outline-none focus:ring-1 focus:ring-brand text-primary text-center';
  const selectClass =
    'border border-blue-200 rounded-lg px-2 py-1 text-sm bg-surface focus:outline-none focus:ring-1 focus:ring-brand text-primary';

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap">
      <span className="text-sm font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
        Rule settings:
      </span>

      {/* Rolling window — always visible */}
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
          <span className="text-xs text-blue-600 dark:text-blue-400">Rolling window</span>
          <input
            type="number"
            className={inputClass}
            min={1}
            max={90}
            value={rollingWindowDays}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              onRollingWindowDaysChange(isNaN(val) ? 7 : val);
            }}
            onBlur={(e) => {
              let val = parseInt(e.target.value, 10);
              if (isNaN(val) || val < 1) val = 1;
              if (val > 90) val = 90;
              onRollingWindowDaysChange(val);
            }}
          />
          <span className="text-xs text-blue-600 dark:text-blue-400">days</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="p-0.5 text-blue-400 hover:text-blue-600">
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              How many calendar days of KPI data to use. Creatives must have data for all days in the window. Applies to all verdict rules.
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Percentile settings — conditional */}
      {showPercentileSettings && (
        <>
          <span className="text-blue-300 dark:text-blue-600 select-none">·</span>

          {/* Default scope selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-blue-600 dark:text-blue-400">Default scope</span>
            <select
              className={selectClass}
              value={settings.defaultScope}
              onChange={(e) =>
                onChange({
                  ...settings,
                  defaultScope: e.target.value as PercentileSettings['defaultScope'],
                })
              }
            >
              {SCOPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-blue-400 dark:text-blue-500">(applies to all conditions unless overridden)</span>
          </div>

          <span className="text-blue-300 dark:text-blue-600 select-none">·</span>

          {/* Minimum peers input */}
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-blue-600 dark:text-blue-400">Minimum peers</span>
              <input
                type="number"
                className={inputClass}
                min={1}
                max={100}
                value={settings.minimumPeers}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= 100) {
                    onChange({ ...settings, minimumPeers: val });
                  }
                }}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="p-0.5 text-blue-400 hover:text-blue-600">
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  Percentile conditions are skipped when fewer than this many creatives exist for comparison
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </>
      )}
    </div>
  );
}
